// Máscaras de digitação ligadas por data-mask. O servidor continua normalizando
// e validando: a máscara só ajuda a digitar, nunca decide o que é válido.

export type MaskKind = 'cep' | 'document' | 'phone' | 'ibge' | 'uf' | 'decimal' | 'brl';

function digits(raw: string): string {
  return raw.replace(/\D/g, '');
}

// Encaixa os caracteres no molde: 0 = dígito, A = letra ou dígito; o resto é literal.
function fit(chars: string, pattern: string): string {
  let out = '';
  let i = 0;
  for (const slot of pattern) {
    if (i >= chars.length) break;
    if (slot === '0' || slot === 'A') {
      out += chars[i];
      i += 1;
    } else {
      out += slot;
    }
  }
  return out;
}

// Reais com centavos automáticos: os dígitos entram da direita ("150050" vira "R$ 1.500,50").
// O servidor normaliza para "1500,50" antes do motor fiscal.
function formatBRL(raw: string): string {
  const d = digits(raw).replace(/^0+/, '').slice(0, 15);
  if (d === '') return '';
  const padded = d.padStart(3, '0');
  const whole = padded.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `R$ ${whole},${padded.slice(-2)}`;
}

// Valor já gravado ("1500", "1500,5", "1500.50", "R$ 1.500,50") vira a máscara sem deslocar centavos.
export function formatInitial(kind: MaskKind, raw: string): string {
  if (kind !== 'brl') return formatMask(kind, raw);
  let text = raw.replace(/R\$|\s/g, '');
  if (text.includes(',')) text = text.replace(/\./g, '').replace(',', '.');
  const [whole = '', cents = ''] = text.split('.');
  if (!/^\d*$/.test(whole) || !/^\d*$/.test(cents) || whole + cents === '') return formatBRL(raw);
  return formatBRL(`${whole}${cents.padEnd(2, '0').slice(0, 2)}`);
}

export function formatMask(kind: MaskKind, raw: string): string {
  switch (kind) {
    case 'brl':
      return formatBRL(raw);
    case 'cep':
      return fit(digits(raw).slice(0, 8), '00000-000');
    case 'ibge':
      return digits(raw).slice(0, 7);
    case 'uf':
      return raw.replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase();
    case 'phone': {
      // Prefixo internacional fica como veio: o servidor aceita qualquer forma discável.
      if (raw.trimStart().startsWith('+')) return raw;
      const d = digits(raw).slice(0, 11);
      return fit(d, d.length > 10 ? '(00) 00000-0000' : '(00) 0000-0000');
    }
    case 'document': {
      // CPF só tem dígitos; CNPJ aceita letras nas 12 primeiras posições (CNPJ alfanumérico).
      const chars = raw.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 14);
      if (chars.length <= 11 && /^\d*$/.test(chars)) return fit(chars, '000.000.000-00');
      return fit(chars, 'AA.AAA.AAA/AAAA-00');
    }
    case 'decimal': {
      // Sem separador de milhar: o motor fiscal lê "1234,56".
      const cleaned = raw.replace(/\./g, ',').replace(/[^\d,]/g, '');
      const [whole = '', ...rest] = cleaned.split(',');
      if (rest.length === 0) return whole;
      return `${whole},${rest.join('').slice(0, 2)}`;
    }
  }
}

// Ao sair do campo, valor completa os centavos ("12" vira "12,00").
export function finishMask(kind: MaskKind, value: string): string {
  if (kind !== 'decimal' || value === '') return value;
  const [whole = '', cents = ''] = value.split(',');
  return `${whole === '' ? '0' : whole},${cents.padEnd(2, '0')}`;
}

// Caracteres que a pessoa digitou (o resto é literal da máscara). No decimal o ponto
// conta porque vira a vírgula; nos outros, ponto e traço são literais.
function significantFor(kind: MaskKind): RegExp {
  if (kind === 'brl') return /\d/;
  return kind === 'decimal' ? /[0-9,.]/ : /[0-9A-Za-z]/;
}

function countSignificant(text: string, significant: RegExp): number {
  let n = 0;
  for (const ch of text) if (significant.test(ch)) n += 1;
  return n;
}

function caretAfter(text: string, count: number, significant: RegExp): number {
  if (count === 0) return 0;
  let seen = 0;
  for (let i = 0; i < text.length; i += 1) {
    if (significant.test(text.charAt(i))) seen += 1;
    if (seen === count) return i + 1;
  }
  return text.length;
}

// Posição do cursor depois de formatar, mantendo-o após o mesmo caractere digitado.
export function caretFor(kind: MaskKind, raw: string, caret: number, formatted: string): number {
  // Centavos automáticos: o cursor fica no fim, onde o próximo dígito entra.
  if (kind === 'brl') return formatted.length;
  const significant = significantFor(kind);
  return caretAfter(formatted, countSignificant(raw.slice(0, caret), significant), significant);
}

export function bindMasks(root: ParentNode = document): void {
  root.querySelectorAll<HTMLInputElement>('input[data-mask]').forEach((input) => {
    if (input.dataset.maskBound === 'true') return;
    const kind = input.dataset.mask as MaskKind;
    input.dataset.maskBound = 'true';
    if (input.value !== '') input.value = formatInitial(kind, input.value);
    input.addEventListener('input', () => {
      const raw = input.value;
      const next = formatMask(kind, raw);
      if (next === raw) return;
      const position = caretFor(kind, raw, input.selectionStart ?? raw.length, next);
      input.value = next;
      if (document.activeElement === input) input.setSelectionRange(position, position);
    });
    input.addEventListener('blur', () => {
      const done = finishMask(kind, input.value);
      if (done === input.value) return;
      input.value = done;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}
